import { useEffect } from "react"

import { ApiQueryName } from "@/models/api"
import type { HttpError, HttpErrorRecord } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

import type { UseUploadMutationVariables } from "../services.types"

export type UseUploadEntitiesOptions = Omit<
  UseMutationOptions<
    unknown,
    HttpError<HttpErrorRecord>,
    UseUploadMutationVariables
  >,
  "mutationFn"
>

const uploadEntitiesFn =
  (endpoint: string) =>
  async ({ file, organizationId }: UseUploadMutationVariables) => {
    try {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("organization_id", organizationId ?? "")

      const response = await httpClient.post(endpoint, formData)

      return response
    } catch (error: unknown) {
      throw toHttpError(error)
    }
  }

/**
 * Uploads a CSV file of entities data
 *
 * @example
 * const {
 *   error,
 *   isError,
 *   isLoading,
 *   mutate
 * } = useUploadEntities(options)
 *
 * mutate({ file, organizationId })
 */
const SITE_UPLOADS_ENDPOINT = "/api/v1/data_manager/site_uploads"

export const useUploadEntities = (options: UseUploadEntitiesOptions = {}) => {
  const queryClient = useQueryClient()

  const uploadFn = uploadEntitiesFn(SITE_UPLOADS_ENDPOINT)

  const mutation = useMutation({
    ...options,
    meta: { ...options.meta, handlesOwnErrors: true },
    mutationFn: uploadFn,
  })

  useEffect(() => {
    if (mutation.isSuccess) {
      void queryClient.invalidateQueries({
        queryKey: [ApiQueryName.SitePreviews],
      })
    }
  }, [mutation.isSuccess, queryClient])

  return mutation
}
